import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, Loader2, Leaf, ArrowLeft, Wallet, CreditCard } from "lucide-react";
import { toast } from "sonner";

export default function Piano() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState("yearly"); // default yearly (highlighted)
  const [pollSession, setPollSession] = useState(sp.get("session_id"));
  const [paypalCfg, setPaypalCfg] = useState(null);
  const [paypalLoading, setPaypalLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/paypal/config");
        setPaypalCfg(data);
      } catch { /* silent — PayPal is optional */ }
    })();
    if (sp.get("paypal") === "success") toast.success("PayPal: pagamento in elaborazione. Ti aggiorniamo appena confermato.");
    if (sp.get("paypal") === "cancel") toast.error("Pagamento PayPal annullato.");
  }, []); // eslint-disable-line

  const subscribePaypal = async () => {
    if (!paypalCfg?.configured) return toast.error("PayPal non ancora configurato dall'amministratore.");
    setPaypalLoading(true);
    try {
      const { data } = await api.post("/paypal/subscribe", { plan });
      if (data.approval_url) window.location.href = data.approval_url;
      else toast.error("PayPal non ha restituito un URL di approvazione");
    } catch (e) { toast.error(errMsg(e, "Errore PayPal")); setPaypalLoading(false); }
  };

  const load = async () => {
    const { data } = await api.get("/subscription/status");
    setStatus(data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!pollSession) return;
    let attempts = 0;
    const iv = setInterval(async () => {
      attempts++;
      try {
        const { data } = await api.get(`/subscription/session/${pollSession}`);
        if (data.payment_status === "paid") {
          clearInterval(iv);
          toast.success("Pagamento confermato — piano Premium attivo!");
          setPollSession(null);
          window.history.replaceState(null, "", "/piano");
          load();
        } else if (attempts > 20) { clearInterval(iv); setPollSession(null); }
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(iv);
  }, [pollSession]);

  const subscribe = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/subscription/checkout", { origin_url: window.location.origin, plan });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e, "Errore checkout")); setLoading(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Cancellare l'abbonamento? Rimarrà attivo fino a fine periodo.")) return;
    try { await api.post("/subscription/cancel"); toast.success("Abbonamento cancellato a fine periodo."); load(); }
    catch (e) { toast.error(errMsg(e, "Errore")); }
  };

  const s = status?.status || "trial";
  const daysLeft = status?.trial_days_left ?? 0;
  const monthly = status?.plans?.monthly?.price_cents ?? 999;
  const yearly = status?.plans?.yearly?.price_cents ?? 9900;
  const yearlyMonthly = (yearly / 12 / 100).toFixed(2).replace(".", ",");
  const yearlySave = ((monthly * 12 - yearly) / 100).toFixed(0);
  const creditEur = status?.credit_cents ? (status.credit_cents / 100).toFixed(2).replace(".", ",") : null;

  return (
    <div className="space-y-8 max-w-3xl mx-auto" data-testid="piano-page">
      <button onClick={()=>navigate(-1)} className="flex items-center gap-1 text-zen-ink2 hover:text-zen-ink text-sm">
        <ArrowLeft size={14}/> Indietro
      </button>
      <div>
        <span className="zen-label">Il tuo piano</span>
        <h1 className="mt-2 font-heading text-4xl">Piano & Abbonamento</h1>
        <p className="text-zen-ink2 mt-1">Prova gratuita di 30 giorni. Scegli il piano che preferisci.</p>
      </div>

      {creditEur && (
        <div data-testid="stripe-credit-card" className="zen-card p-4 flex items-center gap-3 bg-zen-primary/5 border-zen-primary/20">
          <Wallet size={18} className="text-zen-primary"/>
          <div className="flex-1 text-sm">
            <b>€ {creditEur}</b> di credito attivo — verrà scalato automaticamente sulla prossima fattura.
          </div>
        </div>
      )}

      {/* Current status */}
      <div className="zen-card p-6" data-testid="current-plan-card">
        {s === "trial" && (
          <>
            <div className="flex items-center gap-2 text-zen-info mb-2"><Sparkles size={16}/> <span className="zen-label">Prova gratuita</span></div>
            <div className="font-heading text-2xl">Ti restano <span data-testid="trial-days-left" className="text-zen-primary">{daysLeft}</span> giorni</div>
            <p className="text-zen-ink2 mt-1 text-sm">Dopo la prova, passi al piano scelto salvo cancellazione.</p>
          </>
        )}
        {s === "active" && (
          <>
            <div className="flex items-center gap-2 text-zen-primary mb-2"><Check size={16}/> <span className="zen-label">Piano attivo</span></div>
            <div className="font-heading text-2xl">FreelanZen Premium</div>
            {status?.plan_renews_at && (
              <p className="text-zen-ink2 mt-1 text-sm">Rinnovo automatico il {new Date(status.plan_renews_at).toLocaleDateString("it-IT")}.</p>
            )}
            <Button data-testid="cancel-sub-btn" onClick={cancel} variant="outline" className="mt-4 rounded-full text-zen-alert border-zen-alert/30 hover:bg-zen-alert/5">
              Cancella rinnovo
            </Button>
          </>
        )}
        {(s === "expired" || s === "past_due" || s === "cancelled") && (
          <>
            <div className="flex items-center gap-2 text-zen-alert mb-2"><span className="zen-label">Piano scaduto</span></div>
            <div className="font-heading text-2xl">Riattiva il tuo abbonamento</div>
            <p className="text-zen-ink2 mt-1 text-sm">Per continuare a usare FreelanZen serve un piano attivo.</p>
          </>
        )}
      </div>

      {/* Plan toggle + card */}
      {s !== "active" && (
        <>
          {/* Toggle */}
          <div className="flex justify-center">
            <div className="inline-flex bg-zen-bg border border-zen-border rounded-full p-1" data-testid="plan-toggle">
              <button
                data-testid="plan-monthly-btn"
                onClick={()=>setPlan("monthly")}
                className={`px-5 py-2 rounded-full text-sm transition-colors ${plan === "monthly" ? "bg-white shadow-sm text-zen-ink" : "text-zen-ink2 hover:text-zen-ink"}`}
              >
                Mensile
              </button>
              <button
                data-testid="plan-yearly-btn"
                onClick={()=>setPlan("yearly")}
                className={`px-5 py-2 rounded-full text-sm transition-colors flex items-center gap-2 ${plan === "yearly" ? "bg-white shadow-sm text-zen-ink" : "text-zen-ink2 hover:text-zen-ink"}`}
              >
                Annuale
                <span className="text-[10px] bg-zen-primary text-white px-2 py-0.5 rounded-full uppercase tracking-wider">-{yearlySave}€</span>
              </button>
            </div>
          </div>

          <div className="zen-card p-8 relative overflow-hidden" data-testid="premium-plan-card">
            {plan === "yearly" && <div className="absolute top-0 right-0 bg-zen-primary text-white text-xs px-3 py-1 rounded-bl-xl">2 mesi gratis</div>}
            <div className="flex items-center gap-2 mb-4">
              <Leaf size={20} className="text-zen-primary"/>
              <h2 className="font-heading text-2xl">FreelanZen Premium</h2>
            </div>

            {plan === "monthly" ? (
              <>
                <div className="flex items-baseline gap-1 mb-1">
                  <span data-testid="price-display" className="font-heading text-5xl">€ 9,99</span>
                  <span className="text-zen-ink2">/ mese</span>
                </div>
                <p className="text-zen-ink2 text-sm mb-6">IVA inclusa · disdici quando vuoi</p>
              </>
            ) : (
              <>
                <div className="flex items-baseline gap-1 mb-1">
                  <span data-testid="price-display" className="font-heading text-5xl">€ 99,00</span>
                  <span className="text-zen-ink2">/ anno</span>
                </div>
                <p className="text-zen-ink2 text-sm mb-6">Equivale a € {yearlyMonthly}/mese · IVA inclusa · risparmi {yearlySave}€ all&apos;anno</p>
              </>
            )}

            <ul className="space-y-3 mb-8">
              {[
                "Clienti, progetti e attività illimitati",
                "Fatturazione con solleciti AI (Claude Sonnet 4.5)",
                "Inbox unificata Email + WhatsApp",
                "Reportistica con AI insights",
                "Appunti + trascrizione vocale con Whisper",
                "Obiettivi mensili, trimestrali e annuali",
                "Programma referral: 1 mese gratis ogni 3 amici invitati",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <Check size={16} className="text-zen-primary shrink-0 mt-0.5"/>
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <Button
              data-testid="subscribe-btn"
              onClick={subscribe} disabled={loading}
              className="w-full bg-zen-primary hover:bg-zen-primaryHover text-white rounded-full py-6 text-base"
            >
              {loading ? <><Loader2 size={16} className="mr-2 animate-spin"/> Preparo pagamento…</> : <><CreditCard size={16} className="mr-2"/>Paga con carta (Stripe)</>}
            </Button>
            {paypalCfg?.configured && (
              <>
                <div className="flex items-center gap-3 my-4">
                  <div className="h-px bg-zen-border flex-1"/>
                  <span className="text-xs text-zen-ink2 uppercase tracking-widest">oppure</span>
                  <div className="h-px bg-zen-border flex-1"/>
                </div>
                <Button
                  data-testid="subscribe-paypal-btn"
                  onClick={subscribePaypal} disabled={paypalLoading}
                  variant="outline"
                  className="w-full rounded-full py-6 text-base border-[#0070ba] text-[#0070ba] hover:bg-[#0070ba]/5"
                >
                  {paypalLoading ? <><Loader2 size={16} className="mr-2 animate-spin"/> Redirect PayPal…</> : (
                    <span className="flex items-center gap-2">
                      Paga con <b>Pay</b><b className="text-[#003087]">Pal</b>
                    </span>
                  )}
                </Button>
              </>
            )}
            <p className="text-xs text-zen-ink2 text-center mt-3">
              Checkout sicuro tramite Stripe {paypalCfg?.configured ? "o PayPal" : ""} · carte, Apple Pay, Google Pay.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
